import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { CButton, CForm, CFormFeedback, CFormInput, CInputGroup, CModal, CModalBody, CModalFooter, CModalHeader } from '@coreui/react-pro';
import { Formik } from 'formik';
import Cookies from 'js-cookie';
import * as Yup from 'yup';

import { callApi } from '../../apiCaller';
import { toastError } from '../../utils';
import { API_AUTH_SIGNUP, COOKIE_NAMES, MODAL_LOGIN, MY_LEARNING_ROUTES, REGEX_EMAIL } from '../../constants';

import iconLogin from '../../assets/images/iconLogin.svg';

import { setShow } from '../../actions/general';
import { setSubscriberUser } from '../CMS/Subscriber/GlobalStates/CurrentUser/actions';
import { iconEye, iconEyeSlash } from '../../assets/icons/custom';
import CIcon from '@coreui/icons-react';

const validationSchema = (setValidateOnChange) => {
     setValidateOnChange(true);

     return Yup.object().shape({
          firstName: Yup.string().required('First name is required'),
          lastName: Yup.string().required('Last name is required'),
          email: Yup.string().required('Email is required').matches(REGEX_EMAIL, 'Not a valid email'),
          password: Yup.string().required('Password is required').min(8, 'Password must be at least 8 characters'),
     });
};

const RegisterModal = () => {
     const dispatch = useDispatch();
     const { token } = useParams();
     const navigate = useNavigate();
     const [showPassword, setShowPassword] = useState(false);

     const [validateOnChange, setValidateOnChange] = useState(false);

     const initialValues = {
          firstName: '',
          lastName: '',
          email: '',
          password: '',
     };

     const handleCloseRegister = () => {
          dispatch(setShow(null));
     };

     const handleToSignIn = () => {
          dispatch(setShow(MODAL_LOGIN));
     };

     const handleFormRegisterSubmit = (values, { setSubmitting }) => {
          if (token) {
               values.token = token;
          }
          const newValues = { ...values, email: values?.email.toLocaleLowerCase(), firstLogin: true };
          callApi(API_AUTH_SIGNUP, 'POST', newValues).then((response) => {
               setValidateOnChange(false);
               setSubmitting(false);

               if (response.status !== 200) {
                    toastError(response);
                    return;
               }

               Cookies.set(COOKIE_NAMES.TP_IS_LOGGED_IN, 1);
               dispatch(setSubscriberUser(response.data));
               dispatch(setShow(null));

               navigate(MY_LEARNING_ROUTES, { replace: true });
          });
     };

     const handleShowPassword = () => {
          setShowPassword(!showPassword);
     };

     return (
          <>
               <CModal alignment="center" visible className="modal-register" onClose={handleCloseRegister}>
                    <CModalHeader className="d-flex flex-column modal-header">
                         <img src={iconLogin} alt="Register" className="modal-title" id="exampleModalLongTitle" />
                         <h4 className="modal-title" id="exampleModalLongTitle">
                              Create an account
                         </h4>
                         <p className="modal-title" id="exampleModalLongTitle">
                              Create a free CAMS-care account to get access to training products.
                         </p>
                    </CModalHeader>
                    <CModalBody>
                         <Formik
                              initialValues={initialValues}
                              validationSchema={() => validationSchema(setValidateOnChange)}
                              onSubmit={handleFormRegisterSubmit}
                              validateOnChange={validateOnChange}
                              valuedateOnBlur={false}
                         >
                              {({ values, errors, handleChange, handleSubmit, isSubmitting }) => {
                                   return (
                                        <CForm noValidate className="form-create-account" onSubmit={handleSubmit}>
                                             <div className="form-group d-flex flex-column">
                                                  <div className="name-user-input">
                                                       <CInputGroup className="input-custom-wrap">
                                                            <CFormInput
                                                                 type="text"
                                                                 placeholder="First name"
                                                                 className="form-control input-custom"
                                                                 name="firstName"
                                                                 autoComplete="off"
                                                                 invalid={!!errors.firstName}
                                                                 value={values.firstName}
                                                                 onChange={handleChange}
                                                                 disabled={isSubmitting}
                                                            />
                                                            <CFormFeedback invalid>{errors.firstName}</CFormFeedback>
                                                       </CInputGroup>
                                                       <CInputGroup className="input-custom-wrap">
                                                            <CFormInput
                                                                 type="text"
                                                                 placeholder="Last name"
                                                                 className="form-control input-custom"
                                                                 name="lastName"
                                                                 autoComplete="off"
                                                                 invalid={!!errors.lastName}
                                                                 value={values.lastName}
                                                                 onChange={handleChange}
                                                                 disabled={isSubmitting}
                                                            />
                                                            <CFormFeedback invalid>{errors.lastName}</CFormFeedback>
                                                       </CInputGroup>
                                                  </div>
                                                  <CInputGroup className="input-custom-wrap">
                                                       <CFormInput
                                                            type="email"
                                                            placeholder="Email"
                                                            className="form-control input-custom"
                                                            name="email"
                                                            autoComplete="off"
                                                            invalid={!!errors.email}
                                                            value={values.email}
                                                            onChange={handleChange}
                                                            disabled={isSubmitting}
                                                       />
                                                       <CFormFeedback invalid>{errors.email}</CFormFeedback>
                                                  </CInputGroup>
                                                  <CInputGroup className="input-custom-wrap">
                                                       <div className="w-100 position-relative">
                                                            <CFormInput
                                                                 id="password"
                                                                 name="password"
                                                                 type={showPassword ? 'text' : 'password'}
                                                                 placeholder="Password"
                                                                 className="form-control input-custom"
                                                                 autoComplete="off"
                                                                 invalid={!!errors.password}
                                                                 value={values.password}
                                                                 onChange={handleChange}
                                                                 disabled={isSubmitting}
                                                            />
                                                            <CIcon
                                                                 icon={!showPassword ? iconEyeSlash : iconEye}
                                                                 size="xl"
                                                                 className="icon-eye register"
                                                                 onClick={handleShowPassword}
                                                            ></CIcon>
                                                       </div>
                                                       <CFormFeedback invalid className={`${errors.password ? 'show-invalid' : ''}`}>
                                                            {errors.password}
                                                       </CFormFeedback>
                                                  </CInputGroup>
                                                  <button className="btn btn-blue-highlight" type="submit" disabled={isSubmitting}>
                                                       {isSubmitting ? (
                                                            <>
                                                                 Loading<span className="dots-wating"></span>
                                                            </>
                                                       ) : (
                                                            'Register'
                                                       )}
                                                  </button>
                                             </div>
                                        </CForm>
                                   );
                              }}
                         </Formik>
                    </CModalBody>
                    <CModalFooter>
                         <CButton className="btn btn-blue-dark" onClick={handleToSignIn}>
                              {' '}
                              SIGN IN
                         </CButton>
                    </CModalFooter>
               </CModal>
          </>
     );
};

export default RegisterModal;
